import { useEffect, useState } from 'react';
import { ReactFitty } from 'react-fitty';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch } from '../../../../app/redux/hooks';
import { GameInstance } from '../../../api/menu/GameMenuItem';
import { useAddGameToTransactionMutation, useGetGameMenuItemQuery } from '../../../api/menu/menuSlice';
import { setQuantity, setQuantityShow, useMenuItems, useTransaction } from '../../orderSlice';

import { getAvailabilityInfo, parseUtcDate } from './gameAvailability';

export default function Game() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { menuItemId } = useParams();

  const { data: gameMenuItem, error } = useGetGameMenuItemQuery(Number(menuItemId), {
    skip: !menuItemId,
  });

  const [addGameToTransaction, { isLoading }] = useAddGameToTransactionMutation();
  const transaction = useTransaction();
  const menuItems = useMenuItems();

  const [selectedInstance, setSelectedInstance] = useState<GameInstance | undefined>();
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  useEffect(() => {
    if (error) navigate(`/order/add/${menuItemId}`);
  }, [error, menuItemId, navigate]);

  if (!gameMenuItem) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((e.target as any).className === 'pos-modal-overlay') {
      dispatch(setQuantityShow(false));
      dispatch(setQuantity(1));
      navigate('/order');
    }
  };

  const handleInstanceClick = (instance: GameInstance) => {
    if (!instance.isSellable) return;
    setSelectedInstance(instance);
  };

  const handleBackToInstances = () => {
    setSelectedInstance(undefined);
    setSelectedQuantity(1);
  };

  const getUpcomingBookings = (instance: GameInstance) => {
    const nowUtc = Date.now();

    // Sort availability by fromDateTime
    const sortedSlots = [...instance.availability].sort((a, b) => parseUtcDate(a.fromDateTime) - parseUtcDate(b.fromDateTime));

    // Find all upcoming unavailable slots (bookings)
    const upcomingBookings = sortedSlots
      .filter((slot) => {
        const from = parseUtcDate(slot.fromDateTime);
        return from > nowUtc && !slot.available;
      })
      .slice(0, 3); // Get next 3 bookings

    return upcomingBookings;
  };

  const handleAdd = async () => {
    if (!selectedInstance || !gameMenuItem) return;

    const selectedMenuItem = menuItems.find((x) => x.id === Number(menuItemId));
    if (!selectedMenuItem || selectedMenuItem.prices.length === 0) return;

    const transactionId = transaction.length > 0 ? transaction[0].id : 0;
    const unitOfSaleId = selectedMenuItem.prices[0].unitOfSaleId;

    try {
      await addGameToTransaction({
        transactionId,
        menuItemId: Number(menuItemId),
        unitOfSaleId,
        gameId: gameMenuItem.gameId,
        instanceId: selectedInstance.instanceId,
        quantity: selectedQuantity,
        startDateTime: new Date().toISOString(),
        customerName: '',
      }).unwrap();

      navigate('/order');
    } catch (error) {
      console.error('Failed to add game to transaction:', error);
    }
  };

  const fittyDims = () => {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    let maxWidth = '430px';

    if (windowHeight === 800 && windowWidth === 1280) maxWidth = '473px';
    else if (windowHeight === 768 && windowWidth === 1024) maxWidth = '300px';
    else if (windowHeight === 600 && windowWidth === 1024) maxWidth = '300px';

    if (windowHeight >= 900 && windowHeight <= 905) {
      return {
        min: 30,
        max: 50,
        width: maxWidth,
      };
    } else if (windowHeight == 800) {
      return {
        min: 30,
        max: 45,
        width: maxWidth,
      };
    } else if (windowHeight == 768) {
      return {
        min: 25,
        max: 35,
        width: maxWidth,
      };
    } else if (windowHeight >= 600 && windowHeight <= 605) {
      return {
        min: 25,
        max: 35,
        width: maxWidth,
      };
    }
    return { min: 30, max: 60, width: maxWidth };
  };

  const InstanceButton = ({ position }: { position: number }) => {
    const instance = gameMenuItem.instances[position - 1];
    if (!instance) return <div className="pos-item__condiment hidden-button"></div>;

    const availabilityInfo = getAvailabilityInfo(instance);

    const getAvailabilityText = () => {
      if (!instance.isSellable || !availabilityInfo.available) return 'Not Available';
      if (availabilityInfo.timeUntilNextBooking === undefined) return 'Available';
      return `${availabilityInfo.timeUntilNextBooking} ${gameMenuItem.timeQuantifier}${
        availabilityInfo.timeUntilNextBooking !== 1 ? 's' : ''
      } until next booking`;
    };

    return (
      <div
        className={`pos-item__condiment ${!instance.isSellable || !availabilityInfo.available ? 'is-disabled' : ''}`}
        onClick={() => handleInstanceClick(instance)}
      >
        <p className="pos-item__condiment-text">{instance.instanceName}</p>
        <p className="pos-item__condiment-price">{getAvailabilityText()}</p>
      </div>
    );
  };

  return (
    <div className="pos-modal-portal">
      <div className="pos-modal-overlay" onClick={handleOverlayClick}>
        <div className="pos-modal pos-modal_role_item">
          <div className="pos-modal__content">
            <div className="pos-item js-item">
              <div className="pos-item__header">
                <div className="pos-item__info">
                  <h2 className="pos-item__title">
                    <span className="pos-item__title-text js-item-title-text">
                      <ReactFitty style={{ width: fittyDims().width }} minSize={fittyDims().min} maxSize={fittyDims().max}>
                        {selectedInstance ? `${gameMenuItem.instanceName} ${selectedInstance.instanceId}` : gameMenuItem.instanceName}
                      </ReactFitty>
                    </span>
                  </h2>
                </div>
              </div>

              {!selectedInstance && (
                <div className="pos-item__options js-item-options">
                  <div className="pos-item__condiments">
                    <div className="pos-item__condiments-row">
                      <InstanceButton position={1} />
                      <InstanceButton position={2} />
                      <InstanceButton position={3} />
                      <InstanceButton position={4} />
                    </div>
                    <div className="pos-item__condiments-row">
                      <InstanceButton position={5} />
                      <InstanceButton position={6} />
                      <InstanceButton position={7} />
                      <InstanceButton position={8} />
                    </div>
                    <div className="pos-item__condiments-row">
                      <InstanceButton position={9} />
                      <InstanceButton position={10} />
                      <InstanceButton position={11} />
                      <InstanceButton position={12} />
                    </div>
                    <div className="pos-item__condiments-row">
                      <InstanceButton position={13} />
                      <InstanceButton position={14} />
                      <InstanceButton position={15} />
                      <InstanceButton position={16} />
                    </div>
                  </div>
                </div>
              )}

              {selectedInstance &&
                (() => {
                  const availabilityInfo = getAvailabilityInfo(selectedInstance);
                  const upcomingBookings = getUpcomingBookings(selectedInstance);

                  return (
                    <div className="pos-item__options js-item-options">
                      <div className="pos-item__meta" style={{ padding: '20px' }}>
                        <p className="pos-item__meta-label">
                          {availabilityInfo.timeUntilNextBooking !== undefined
                            ? `${availabilityInfo.timeUntilNextBooking} ${gameMenuItem.timeQuantifier}${
                                availabilityInfo.timeUntilNextBooking !== 1 ? 's' : ''
                              } until next booking`
                            : 'Available - no upcoming bookings'}
                        </p>

                        {upcomingBookings.length > 0 && (
                          <>
                            <p className="pos-item__meta-label" style={{ marginTop: '20px', fontWeight: 'bold' }}>
                              Upcoming Bookings:
                            </p>
                            {upcomingBookings.map((booking, index) => {
                              const fromTime = new Date(parseUtcDate(booking.fromDateTime)).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                              const toTime = new Date(parseUtcDate(booking.toDateTime)).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                              return (
                                <p key={index} className="pos-item__meta-text" style={{ fontSize: '14px', marginTop: '5px' }}>
                                  {fromTime} - {toTime}
                                  {booking.customerName && ` (${booking.customerName})`}
                                </p>
                              );
                            })}
                          </>
                        )}

                        <p className="pos-item__meta-label" style={{ marginTop: '20px' }}>
                          How many {gameMenuItem.timeQuantifier}s would you like?
                        </p>
                      </div>
                    </div>
                  );
                })()}

              {selectedInstance &&
                (() => {
                  const availabilityInfo = getAvailabilityInfo(selectedInstance);
                  const maxQuantity = availabilityInfo.timeUntilNextBooking ?? 999;
                  const shortcuts = [
                    { label: '1 hr', value: 60 },
                    { label: '1.5 hr', value: 90 },
                    { label: '2 hr', value: 120 },
                  ];

                  return (
                    <>
                      <div className="pos-item__condiments-row" style={{ padding: '0 20px', marginBottom: '14px' }}>
                        {shortcuts.map((shortcut) => {
                          const disabled = shortcut.value > maxQuantity;
                          const active = selectedQuantity === shortcut.value;
                          return (
                            <div
                              key={shortcut.value}
                              className={`pos-item__condiment${disabled ? ' is-disabled' : ''}${active ? ' is-active' : ''}`}
                              style={{ justifyContent: 'center' }}
                              onClick={() => !disabled && setSelectedQuantity(shortcut.value)}
                            >
                              <p className="pos-item__condiment-text" style={{ marginBottom: 0 }}>
                                {shortcut.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <div className="pos-item__quantity">
                        <p className="pos-item__quantity-label">QTY</p>
                        <div className="pos-quantity pos-quantity_role_item">
                          <button
                            className="pos-quantity__control"
                            type="button"
                            onClick={() => setSelectedQuantity(selectedQuantity - 1)}
                            disabled={selectedQuantity === 1}
                          >
                            -
                          </button>
                          <div className="pos-quantity__value">{selectedQuantity}</div>
                          <button
                            className="pos-quantity__control"
                            type="button"
                            onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                            disabled={selectedQuantity >= maxQuantity}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}

              <div className="pos-item__meta">
                <p className="pos-item__meta-label"></p>
                <p className="pos-item__meta-text">
                  {selectedInstance ? (
                    <>
                      <button
                        className="pos-item__add-button"
                        onClick={handleBackToInstances}
                        style={{ marginRight: '10px' }}
                        disabled={isLoading}
                      >
                        Back
                      </button>
                      <button className="pos-item__add-button" onClick={handleAdd} disabled={isLoading}>
                        {isLoading ? 'Adding...' : 'Add'}
                      </button>
                    </>
                  ) : (
                    <button className="pos-item__add-button" onClick={() => navigate('/order')}>
                      Cancel
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
